import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomInput from "../../../components/CustomInput";
import CustomButton from "../../../components/CustomButton";
import CustomSelect from "../../../components/CustomSelectInput";
import { AppDispatch, RootState } from "../../../store/store";
import useCustomStyles from "../../../hooks/useCustomStyles";
import { header2Styles } from "../../../assets/styles/header2";
import { selectState } from "../../../store/slices/themeSlice";
import {
  addPartnerClientId,
  deletePartnerClientId,
  updatePartnerClientId,
} from "../../../store/actions/partnerClientIdsActions";
import { selectTranslationState } from "../../../store/slices/languageSlice";
import { resetState } from "../../../store/slices/partnerClientIdsSlice";

interface PartnerClientIdModalProps {
  isOpen: boolean;
  mode: "add" | "edit";
  /** In edit mode, hide the in-modal "Delete Client Id" button — the row now
   * has a separate cancel icon that opens the dedicated cancel modal. */
  hideDelete?: boolean;
  existingClientId?: {
    id: string;
    clientId: string;
    users_limit: number;
    users_count: number; 
    businessId?: string;
    business?: string;
    note?: string;
    type: string;
  };
  onConfirm: () => void;
  onCancel: () => void;
}

const PartnerClientIdModal: React.FC<PartnerClientIdModalProps> = ({
  isOpen,
  mode,
  hideDelete,
  existingClientId,
  onConfirm,
  onCancel,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { t } = useSelector(selectTranslationState);
  const { theme } = useSelector(selectState);
  const { user } = useSelector((state: RootState) => state.auth_slice);
  const { businesses } = useSelector(
    (state: RootState) => state.accessCode_slice
  );
  const classes = useCustomStyles(header2Styles, theme);
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error } = useSelector(
    (state: RootState) => state.partnerClientIds_slice
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  
  console.log('existingClientId in modal', existingClientId)
  // ✅ Close modal on outside click
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onCancel();
        dispatch(resetState())
      }
    };
    if (isOpen) document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, onCancel]);

  // ✅ Formik setup
  const formik = useFormik({
    initialValues: {
      clientId: existingClientId?.clientId || "",
      users_limit: existingClientId?.users_limit || 1,
      users_count: existingClientId?.users_count || 0, // For validation only
      businessId:
        existingClientId?.business || user?.business?.id || "",
      note: existingClientId?.note || "",
      type: existingClientId?.type || "Individual ID",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      clientId: Yup.string().required("Client ID is required"),
      users_limit: Yup.number()
    .min(1, "Must be at least 1")
    .test(
      "greaterThanUsersCount",
      "Users limit cannot be less than current users count",
      function (value) {
        const { users_count } = this.parent;
        return (value ?? 0) >= users_count;
      }
    )
    .required("Users limit is required"),
      type: Yup.string()
        .oneOf(["Business ID", "Individual ID"])
        .required("Type is required"),
      note: Yup.string().max(200, "Max 200 characters"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          ...values,
          users_limit:
            values.type === "Individual ID" ? 1 : values.users_limit,
        };

        if (mode === "edit" && existingClientId) {
          // ✅ Send in { id, updates } format
          await dispatch(
            updatePartnerClientId({
              token: user?.token || "",
              id: existingClientId.id,
              updates: {clientId: payload.clientId, users_limit: payload.users_limit, businessId: payload.businessId},
            })
          ).unwrap();
        } else {
          await dispatch(addPartnerClientId({token: user?.token || "", newData: [payload]})).unwrap();
        }

        onConfirm();
        if(mode === 'add'){resetForm();}
        
      } catch (err) {
        console.error("Error saving partner client ID:", err);
      }
    },
  });

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    isValid,
    dirty,
    setFieldValue,
  } = formik;

  const handleDelete = async () => {
    if (!existingClientId) return;
    try{
      await dispatch(deletePartnerClientId({token: user?.token || "",id: existingClientId.id})).unwrap(); 
       onConfirm()
    }catch(err){
      console.error("Error deleting partner client ID:", err);
    }   
   
  }

  useEffect(() => {
    console.log('existingClientId', existingClientId)
    console.log('form values changed values errors isvalid dirty', values, errors, isValid, dirty)
  }
  , [values, errors, isValid, dirty])
  
 const handleCancelDelete = () => {  
    setShowDeleteConfirm(false);
    dispatch(resetState())
  }
  if (!isOpen) return null;

  return (
    <div className={classes.overlay}>
      <div className={classes.modal} ref={modalRef}>
        <h2 className={classes.headerTextLogOutModal}>
          {mode === "edit" ? "EDIT CLIENT ID" : "ADD NEW CLIENT ID"}
        </h2>

        <form onSubmit={handleSubmit} className={classes.notificationForm}>
          <CustomInput
            label="Client ID"
            name="clientId"
            type="text"
            value={values.clientId}
            onChange={handleChange}
            error={errors.clientId || ""}
          />
        { mode === 'add' &&
          <CustomSelect
            label="Type"
            name="type"
            value={values.type}
            options={[
              { id: "Business ID", name: "Business ID" },
              { id: "Individual ID", name: "Individual ID" },
            ]}
            onChange={(e) => setFieldValue("type", e.target.value)}
            placeholder="Select type"
            error={errors.type || ""}
          />
        }
          {values.type === "Business ID" && (
            <CustomInput
              label="Users Limit"
              name="users_limit"
              type="number"
              value={values.users_limit}
              onChange={handleChange}
              error={errors.users_limit || ""}
            />
          )}

          {user && user.business && user.business.name === "elisyan" && mode !== 'edit' && (
            <CustomSelect
              label="Business"
              name="businessId"
              value={values.businessId}
              options={
                businesses
                  ? businesses
                      .filter((b: any) => b.type === "partner")
                      .map((b: any) => ({
                        id: b.id,
                        name: b.name,
                      }))
                  : []
              }
              onChange={(e) => setFieldValue("businessId", e.target.value)}
              placeholder="Select a business"
              error={errors.businessId || ""}
            />
          )}

         {showDeleteConfirm ? (
          <>
            <p>Are you sure you want to delete this Client ID?</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <CustomButton
                title="CONFIRM DELETE"
                name="confirmDeleteButton"
                type="button"
                handleClick={handleDelete}
                loading={loading}
                disabled={loading}
                variant="danger"
              />
              <CustomButton
                title="CANCEL"
                name="cancelDeleteButton"
                type="button"
                handleClick={handleCancelDelete}
                loading={false}
                disabled={loading}
                variant="secondary"
              />
            </div>
          </>
         ):(
          <>
          <CustomButton
            title={mode === "edit" ? "Update Client Id" : "Add Client Id"}
            name="partnerClientIdButton"
            type="submit"
            handleClick={() => {}}
            loading={loading}
            disabled={!isValid || !dirty || loading}
          />
          {mode === "edit" && !hideDelete && (
            <CustomButton
            title={'Delete Client Id'}
            name="deletePartnerClientIdButton"
            type="submit"
            handleClick={() => setShowDeleteConfirm(true)}
            loading={false}
            disabled={loading}
            variant="secondary"
          />
          )}</>
         )}
         
          {error && <p className={classes.errorText}>{error}</p>}
        </form>
      </div>
    </div>
  );
};

export default PartnerClientIdModal;
